import { Link } from "@inertiajs/react"
import { ArrowRight } from "lucide-react"
import { SectionReveal } from "@/components/marketing/section-reveal"

type IntegrationNode = {
  name: string
  status: "Demo adapter"
  image: string
  slug: string
  className: string
}

const integrationNodes: readonly IntegrationNode[] = [
  {
    name: "Slack",
    status: "Demo adapter",
    image: "/integration-logos/resolto-slack.svg",
    slug: "slack",
    className: "integration-node--left-top",
  },
  {
    name: "PostgreSQL",
    status: "Demo adapter",
    image: "/integration-logos/resolto-postgres.svg",
    slug: "postgres",
    className: "integration-node--left-middle",
  },
  {
    name: "Stripe",
    status: "Demo adapter",
    image: "/integration-logos/resolto-stripe.svg",
    slug: "stripe",
    className: "integration-node--left-bottom",
  },
  {
    name: "Sentry",
    status: "Demo adapter",
    image: "/integration-logos/resolto-sentry.svg",
    slug: "sentry",
    className: "integration-node--right-top",
  },
  {
    name: "Linear",
    status: "Demo adapter",
    image: "/integration-logos/resolto-linear.svg",
    slug: "linear",
    className: "integration-node--right-middle",
  },
  {
    name: "GitHub",
    status: "Demo adapter",
    image: "/integration-logos/resolto-github.svg",
    slug: "github",
    className: "integration-node--right-bottom",
  },
] as const

const paths = [
  "M 82 58 Q 220 58 380 150",
  "M 82 150 Q 230 150 380 150",
  "M 82 242 Q 220 242 380 150",
  "M 678 58 Q 540 58 380 150",
  "M 678 150 Q 530 150 380 150",
  "M 678 242 Q 540 242 380 150",
] as const

export function IntegrationBeamSection() {
  return (
    <section className="home-integration-section">
      <SectionReveal className="marketing-shell integration-section__inner">
        <header className="integration-section__heading">
          <span>Approved evidence and escalation</span>
          <h2>Connect only what each workflow is allowed to use.</h2>
          <p>
            Every connector declares its capabilities, risk, data classes, and allowed operations
            before a case can use it.
          </p>
        </header>

        <section
          className="integration-diagram"
          aria-label="Fictional Slack, PostgreSQL, Stripe, Sentry, Linear, and GitHub demo adapters feed bounded evidence and escalations into Resolto. No live provider is connected."
        >
          <span className="integration-diagram__glow" aria-hidden="true" />
          <svg viewBox="0 0 760 300" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <linearGradient
                id="texly-beam-left"
                x1="82"
                y1="0"
                x2="380"
                y2="0"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#1297FD" />
                <stop offset="1" stopColor="#8BCAF9" />
              </linearGradient>
              <linearGradient
                id="texly-beam-right"
                x1="678"
                y1="0"
                x2="380"
                y2="0"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#1297FD" />
                <stop offset="1" stopColor="#8BCAF9" />
              </linearGradient>
            </defs>
            {paths.map((path, index) => (
              <g key={path}>
                <path className="integration-diagram__path" d={path} />
                <path
                  className="integration-diagram__beam"
                  d={path}
                  stroke={index < 3 ? "url(#texly-beam-left)" : "url(#texly-beam-right)"}
                  style={{
                    animationDelay: `${index * 0.35}s`,
                  }}
                />
              </g>
            ))}
          </svg>

          {integrationNodes.map(({ name, status, image, slug, className }) => (
            <div
              key={name}
              className={`integration-node ${className}`}
              data-status={status.toLowerCase()}
              data-integration={slug}
              role="img"
              aria-label={`${name}. ${status}.`}
            >
              <span className="integration-node__surface">
                {/* biome-ignore lint/performance/noImgElement: These tiny local SVG badges are already optimized source assets. */}
                <img src={image} alt="" aria-hidden="true" />
              </span>
              <span className="integration-node__name">{name}</span>
            </div>
          ))}

          <div className="integration-node integration-node--texly">
            <span className="integration-node__surface">
              {/* biome-ignore lint/performance/noImgElement: This tiny local SVG is already the optimized brand source. */}
              <img src="/resolto-mark.svg" alt="Resolto" />
            </span>
          </div>
        </section>

        <div className="integration-section__footer">
          <p>
            Functional demo adapters only. No live third-party provider is connected or represented
            as commissioned.
          </p>
          <Link href="/integrations">
            View channels and integrations
            <ArrowRight aria-hidden="true" />
          </Link>
        </div>
      </SectionReveal>
    </section>
  )
}
